import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  ArrowLeft,
  ArrowsClockwise,
  CheckCircle,
  Warning,
  XCircle,
  MagnifyingGlass,
  Shield,
  ListMagnifyingGlass,
} from "@phosphor-icons/react";

/**
 * /admin/ofac-hits — Compliance Officer review queue
 *
 * Surfaces the ofac_screening_hits MongoDB collection populated by
 * services/ofac_screening.screen_name(). Compliance Officer reviews each
 * hit and records a CLEAR (false positive) or CONFIRM (true match → file
 * SAR + block customer) decision with an audit reason ≥10 chars.
 *
 * Also exposes:
 *   • SDN list refresh status + manual force-refresh
 *   • Ad-hoc screening tool (manual lookup that doesn't pollute the queue)
 *   • Audit trail tabs (open / cleared / confirmed / all)
 */

function StatusBadge({ status }) {
  const styles = {
    open: "bg-[#CBA35C]/15 text-[#8C6B21] border-[#CBA35C]/40",
    cleared: "bg-emerald-50 text-emerald-800 border-emerald-200",
    confirmed: "bg-rose-50 text-rose-800 border-rose-200",
  };
  return (
    <span
      className={`mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full border ${styles[status] || styles.open}`}
      data-testid={`ofac-hit-status-${status}`}
    >
      {status}
    </span>
  );
}

function MatchScoreBar({ score }) {
  // 0-100 color ramp: 85-92 amber (likely false pos), 92-97 orange, 97+ red
  const pct = Math.max(0, Math.min(100, score));
  const color = score >= 97 ? "#B04E2F" : score >= 92 ? "#D96C4A" : "#CBA35C";
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-1.5 bg-[#E5E0D8] rounded-full overflow-hidden">
        <div className="h-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <p className="mono text-xs" style={{ color }}>{pct.toFixed(1)}%</p>
    </div>
  );
}

function HitRow({ hit, onDecide }) {
  const [busy, setBusy] = useState(false);
  const [showDecide, setShowDecide] = useState(null);  // "cleared" | "confirmed" | null
  const [reason, setReason] = useState("");

  const submit = async () => {
    if (reason.trim().length < 10) return;
    setBusy(true);
    try {
      await onDecide(hit.id, showDecide, reason.trim());
      setShowDecide(null);
      setReason("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-[#E5E0D8] rounded-2xl p-5 bg-white" data-testid={`ofac-hit-${hit.id}`}>
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={hit.status} />
            <p className="serif text-lg" data-testid="ofac-hit-input-name">{hit.input_name}</p>
          </div>
          <p className="text-xs text-[#4A5C50] mt-1 mono">
            Matched: <span className="font-semibold text-[#112318]">{hit.matched_name}</span>
          </p>
          {hit.matched_entity && (
            <p className="text-xs text-[#4A5C50] mt-0.5 mono">
              SDN entity #{hit.matched_entity.ent_num} · {hit.matched_entity.type} · program <span className="text-[#B04E2F] font-semibold">{hit.matched_entity.program}</span>
            </p>
          )}
        </div>
        <div className="text-right">
          <MatchScoreBar score={hit.match_score} />
          <p className="text-[10px] text-[#4A5C50] mt-1 mono">{new Date(hit.created_at).toLocaleString()}</p>
        </div>
      </div>

      {hit.status === "open" && (
        <div className="mt-4 pt-4 border-t border-[#E5E0D8]">
          {showDecide === null ? (
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowDecide("cleared")}
                className="rounded-xl border-emerald-700 text-emerald-700 hover:bg-emerald-50 text-xs"
                data-testid={`ofac-hit-clear-${hit.id}`}
              >
                <CheckCircle size={14} weight="duotone" /> Clear (false positive)
              </Button>
              <Button
                size="sm"
                onClick={() => setShowDecide("confirmed")}
                className="rounded-xl bg-[#B04E2F] hover:bg-[#9A4329] text-white text-xs"
                data-testid={`ofac-hit-confirm-${hit.id}`}
              >
                <XCircle size={14} weight="duotone" /> Confirm match (file SAR)
              </Button>
            </div>
          ) : (
            <div data-testid={`ofac-hit-reason-form-${hit.id}`}>
              <p className="text-xs text-[#4A5C50] mb-2">
                {showDecide === "cleared"
                  ? "Audit reason for CLEAR — explain why this is a false positive (≥10 characters):"
                  : "Audit reason for CONFIRMED MATCH — describe what evidence supports this (≥10 characters):"}
              </p>
              <textarea
                rows={3}
                className="w-full text-sm border border-[#E5E0D8] rounded-xl p-2 mono"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={
                  showDecide === "cleared"
                    ? "e.g. Name collision — SDN entity is Syrian regime official, our customer is a US resident born 1995, no DOB or geographic overlap"
                    : "e.g. Customer DOB + nationality match SDN record · IP geo Damascus · further KYC docs decline. SAR filed reference NNNN."
                }
              />
              <div className="flex gap-2 mt-2">
                <Button
                  size="sm"
                  onClick={submit}
                  disabled={reason.trim().length < 10 || busy}
                  className={`rounded-xl text-xs text-white ${
                    showDecide === "cleared" ? "bg-emerald-700 hover:bg-emerald-800" : "bg-[#B04E2F] hover:bg-[#9A4329]"
                  }`}
                  data-testid={`ofac-hit-submit-${hit.id}`}
                >
                  {busy ? "Saving…" : `Record ${showDecide.toUpperCase()}`}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setShowDecide(null); setReason(""); }} className="rounded-xl text-xs">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {hit.status !== "open" && (
        <div className="mt-4 pt-4 border-t border-[#E5E0D8] text-xs text-[#4A5C50]">
          <p>
            Decided by <span className="font-semibold text-[#112318]">{hit.decided_by || "—"}</span> at{" "}
            <span className="mono">{hit.decided_at ? new Date(hit.decided_at).toLocaleString() : "—"}</span>
          </p>
          {hit.decision_reason && (
            <p className="mt-2 italic">"{hit.decision_reason}"</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function OfacHitsAdmin() {
  const [status, setStatus] = useState("open");
  const [hits, setHits] = useState([]);
  const [ofacStatus, setOfacStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const [adhoc, setAdhoc] = useState({ name: "", result: null, busy: false });

  const loadHits = useCallback(async (s = status) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/ofac/hits?status=${s}&limit=200`);
      setHits(r.data.hits || []);
    } finally {
      setLoading(false);
    }
  }, [status]);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get("/admin/ofac/status");
      setOfacStatus(r.data);
    } catch {
      setOfacStatus({ ok: false });
    }
  }, []);

  useEffect(() => { loadHits(status); }, [loadHits, status]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const forceRefresh = async () => {
    setRefreshBusy(true);
    try {
      await api.post("/admin/ofac/refresh");
      await loadStatus();
    } finally {
      setRefreshBusy(false);
    }
  };

  const decide = async (hitId, decision, reason) => {
    await api.put(`/admin/ofac/hits/${hitId}`, { decision, reason });
    await loadHits();
  };

  const runAdhoc = async () => {
    if (!adhoc.name.trim()) return;
    setAdhoc((a) => ({ ...a, busy: true, result: null }));
    try {
      const r = await api.post("/admin/ofac/screen", { name: adhoc.name.trim() });
      setAdhoc((a) => ({ ...a, busy: false, result: r.data }));
    } catch (e) {
      setAdhoc((a) => ({ ...a, busy: false, result: { error: e?.message } }));
    }
  };

  const tabs = ["open", "confirmed", "cleared", "all"];

  return (
    <div className="min-h-screen bg-[#FBF9F6] text-[#112318]" data-testid="ofac-hits-admin">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-[#4A5C50] hover:text-[#184A30]" data-testid="ofac-back">
          <ArrowLeft size={14} /> Back to Admin
        </Link>
        <header className="mt-6 flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Compliance · BSA/AML</p>
            <h1 className="serif text-4xl mt-1 flex items-center gap-3">
              <Shield size={32} weight="duotone" className="text-[#184A30]" /> OFAC SDN Review Queue
            </h1>
            <p className="text-[#4A5C50] mt-2 leading-relaxed max-w-2xl text-sm">
              Each row is a name match against the US Treasury SDN list (
              <a href="https://www.treasury.gov/ofac/downloads/sdn.csv" target="_blank" rel="noopener noreferrer" className="underline text-[#184A30]">source</a>
              ) detected at registration or card-issuance time. Decisions are
              retained 5 years per BSA recordkeeping requirements.
            </p>
          </div>
        </header>

        {/* SDN list health */}
        <section className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid="ofac-status-panel">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Sanctions list cache</p>
              <h2 className="serif text-2xl mt-0.5">SDN list freshness</h2>
            </div>
            <Button
              size="sm"
              onClick={forceRefresh}
              disabled={refreshBusy}
              className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white text-xs"
              data-testid="ofac-refresh-btn"
            >
              <ArrowsClockwise size={14} weight="bold" /> {refreshBusy ? "Refreshing…" : "Force refresh now"}
            </Button>
          </div>
          {ofacStatus && (
            <div className="mt-4 grid sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">SDN entities</p>
                <p className="mono font-semibold text-[#184A30] text-lg" data-testid="ofac-sdn-count">{(ofacStatus.sdn_entries || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">Aliases (a.k.a.)</p>
                <p className="mono font-semibold text-[#184A30] text-lg">{(ofacStatus.aliases || 0).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">Open hits</p>
                <p className="mono font-semibold text-[#B04E2F] text-lg" data-testid="ofac-open-count">{ofacStatus.open_hits || 0}</p>
              </div>
              <div>
                <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">Match threshold</p>
                <p className="mono font-semibold text-[#184A30] text-lg">{ofacStatus.match_threshold}%</p>
              </div>
            </div>
          )}
          {ofacStatus?.last_refreshed_at && (
            <p className="text-xs text-[#4A5C50] mt-3 mono">
              Last refreshed: {new Date(ofacStatus.last_refreshed_at).toLocaleString()}
            </p>
          )}
        </section>

        {/* Ad-hoc lookup */}
        <section className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid="ofac-adhoc-panel">
          <div className="flex items-center gap-2">
            <MagnifyingGlass size={18} weight="duotone" className="text-[#184A30]" />
            <h2 className="serif text-xl">Ad-hoc name lookup</h2>
          </div>
          <p className="text-xs text-[#4A5C50] mt-1">
            Manual screening for a single name. Does NOT add to the review queue — purely exploratory.
          </p>
          <div className="mt-3 flex gap-2 flex-wrap">
            <input
              type="text"
              value={adhoc.name}
              onChange={(e) => setAdhoc({ ...adhoc, name: e.target.value })}
              placeholder="e.g. John Smith"
              className="flex-1 min-w-[200px] border border-[#E5E0D8] rounded-xl px-3 py-2 text-sm"
              data-testid="ofac-adhoc-input"
              onKeyDown={(e) => { if (e.key === "Enter") runAdhoc(); }}
            />
            <Button
              onClick={runAdhoc}
              disabled={adhoc.busy || !adhoc.name.trim()}
              className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white text-sm"
              data-testid="ofac-adhoc-run"
            >
              {adhoc.busy ? "Screening…" : "Screen"}
            </Button>
          </div>
          {adhoc.result && (
            <div
              className={`mt-3 p-3 rounded-xl border text-sm ${
                adhoc.result.hit
                  ? "bg-rose-50 border-rose-200 text-rose-900"
                  : "bg-emerald-50 border-emerald-200 text-emerald-900"
              }`}
              data-testid="ofac-adhoc-result"
            >
              {adhoc.result.hit ? (
                <>
                  <p className="font-semibold">⚠ Match found ({adhoc.result.match_score?.toFixed(1)}%)</p>
                  <p className="mono text-xs mt-1">{adhoc.result.matched_name}</p>
                  {adhoc.result.matched_entity && (
                    <p className="mono text-xs">
                      SDN #{adhoc.result.matched_entity.ent_num} · {adhoc.result.matched_entity.type} · {adhoc.result.matched_entity.program}
                    </p>
                  )}
                </>
              ) : (
                <p className="font-semibold">✓ No match against SDN list</p>
              )}
            </div>
          )}
        </section>

        {/* Tabs */}
        <section className="mt-6">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <ListMagnifyingGlass size={20} weight="duotone" className="text-[#184A30]" />
            <h2 className="serif text-2xl">Review queue</h2>
            <div className="ml-auto flex gap-1 bg-white border border-[#E5E0D8] rounded-xl p-1" data-testid="ofac-tabs">
              {tabs.map((t) => (
                <button
                  key={t}
                  onClick={() => setStatus(t)}
                  className={`text-xs px-3 py-1 rounded-lg mono uppercase tracking-widest transition ${
                    status === t ? "bg-[#184A30] text-white" : "text-[#4A5C50] hover:bg-[#FBF9F6]"
                  }`}
                  data-testid={`ofac-tab-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-[#4A5C50] mono">Loading…</p>
          ) : hits.length === 0 ? (
            <div className="rounded-2xl border border-[#E5E0D8] bg-white p-8 text-center" data-testid="ofac-empty">
              <Warning size={32} className="mx-auto text-[#CBA35C]" weight="duotone" />
              <p className="serif text-lg mt-3">No {status === "all" ? "" : status} hits</p>
              <p className="text-sm text-[#4A5C50] mt-1">
                {status === "open"
                  ? "The screening pipeline is running clean — no compliance review pending."
                  : "Try a different filter."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {hits.map((h) => (
                <HitRow key={h.id} hit={h} onDecide={decide} />
              ))}
            </div>
          )}
        </section>

        <footer className="mt-10 text-xs text-[#4A5C50]">
          See <Link to="/admin/setup" className="text-[#184A30] underline">Setup Checklist</Link> for environment variables.
          Full compliance program: <span className="mono">/app/memory/COMPLIANCE_PROGRAM.md</span>.
        </footer>
      </div>
    </div>
  );
}
